import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EventRow } from "../components/EventRow.js";
import { WhoFilter } from "../components/WhoFilter.js";
import { api } from "../lib/api.js";
import {
  addDays,
  datesCovered,
  dayName,
  dayNumber,
  today,
  weekLabel,
  weekStart,
} from "../lib/dates.js";
import { useSession } from "../lib/session.js";
import type { Occurrence } from "../lib/types.js";

/**
 * The default screen, and the one Nick opens twenty times a week.
 *
 * It answers "what's today" with no interaction: today is anchored at the top
 * of the visible list, raised and marked. Empty days collapse to a hairline —
 * a grey "no events" pill for every quiet day would make a calm week look like
 * a broken screen.
 */
export function Week(): React.JSX.Element {
  const { zone, people, canEdit } = useSession();
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(() => today(zone));
  const [filter, setFilter] = useState<string[]>([]);
  const [whoOpen, setWhoOpen] = useState(false);
  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);

  const start = weekStart(anchor, zone);
  const end = addDays(start, 7, zone);
  const todayDate = today(zone);

  const load = useCallback(async () => {
    const data = await api.events(start, end);
    setOccurrences(data.occurrences);
  }, [start, end]);

  useEffect(() => {
    setOccurrences(null);
    void load();
  }, [load]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(start, i, zone)),
    [start, zone],
  );

  /** Occurrences keyed by every day they cover, so a trip appears on each. */
  const byDay = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    if (occurrences === null) return map;

    const visible =
      filter.length === 0
        ? occurrences
        : occurrences.filter((o) => o.people.some((p) => filter.includes(p.id)));

    for (const occurrence of visible) {
      for (const date of datesCovered(occurrence.starts_at, occurrence.ends_at, zone)) {
        if (!map.has(date)) map.set(date, []);
        map.get(date)?.push(occurrence);
      }
    }

    for (const list of map.values()) sortForDay(list);

    return map;
  }, [occurrences, filter, zone]);

  const anythingThisWeek = days.some((d) => (byDay.get(d)?.length ?? 0) > 0);

  return (
    <div className="screen">
      <header className="week__head">
        <button
          type="button"
          className="arrow"
          aria-label="Previous week"
          onClick={() => setAnchor(addDays(start, -7, zone))}
        >
          ←
        </button>
        <h1>{weekLabel(start, zone)}</h1>
        <button
          type="button"
          className="arrow"
          aria-label="Next week"
          onClick={() => setAnchor(addDays(start, 7, zone))}
        >
          →
        </button>
      </header>

      <div className="navstrip">
        <button type="button" onClick={() => setAnchor(addDays(start, -7, zone))}>
          Prev
        </button>
        <button
          type="button"
          className="navstrip__today"
          onClick={() => {
            setAnchor(todayDate);
            setFilter([]);
          }}
        >
          Today
        </button>
        <button type="button" onClick={() => setAnchor(addDays(start, 7, zone))}>
          Next
        </button>
        <button
          type="button"
          className={filter.length > 0 ? "is-filtered" : ""}
          aria-expanded={whoOpen}
          onClick={() => setWhoOpen((open) => !open)}
        >
          Who
        </button>
      </div>

      {whoOpen && (
        <WhoFilter
          people={people}
          selected={filter}
          onClear={() => setFilter([])}
          onToggle={(id) =>
            setFilter((current) =>
              current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
            )
          }
        />
      )}

      <div className="week__days">
        {days.map((date) => {
          const list = byDay.get(date) ?? [];
          const isToday = date === todayDate;

          return (
            <section key={date} className={`day${isToday ? " is-today" : ""}`}>
              <button
                type="button"
                className="day__stamp"
                onClick={() => void navigate(`/day/${date}`)}
              >
                <span className="day__name">{dayName(date, zone)}</span>
                <span className="day__number">{dayNumber(date, zone)}</span>
                {isToday && <span className="day__today">Today</span>}
              </button>

              <div className="day__events">
                {list.length === 0 ? (
                  canEdit ? (
                    <button
                      type="button"
                      className="day__empty"
                      onClick={() => void navigate(`/event/new?date=${date}`)}
                    >
                      + add
                    </button>
                  ) : (
                    <span className="day__empty is-quiet" />
                  )
                ) : (
                  list.map((occurrence) => (
                    <EventRow
                      key={`${occurrence.occurrence_id}:${date}`}
                      occurrence={occurrence}
                      date={date}
                      zone={zone}
                      onOpen={() =>
                        void navigate(
                          `/event/${occurrence.event_id}?occ=${encodeURIComponent(occurrence.occurrence_id)}`,
                        )
                      }
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}

        {occurrences !== null && !anythingThisWeek && (
          <p className="week__clear">
            {filter.length > 0 ? "Nobody selected has anything on." : "A clear week. Rare."}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The order events take within one day.
 *
 * All-day above timed, then by when each began. Sorting by the start rather
 * than by anything to do with today is what keeps a multi-day run in the same
 * position on every day it covers: a trip that started on Monday sorts above
 * Wednesday's birthday on Monday, Tuesday and Wednesday alike, so the list
 * reads as one continuous thing instead of shuffling under it.
 */
function sortForDay(list: Occurrence[]): void {
  list.sort((a, b) => {
    if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
    return a.starts_at.localeCompare(b.starts_at);
  });
}

/** Exported for the day view, which shares the grouping rule. */
export function occurrencesOn(
  occurrences: Occurrence[],
  date: string,
  zone: string,
): Occurrence[] {
  // Timed events span too — a flight out on Friday night is on Saturday's
  // list as well, which is where the person reading it will look for it.
  const here = occurrences.filter((o) =>
    datesCovered(o.starts_at, o.ends_at, zone).includes(date),
  );
  sortForDay(here);
  return here;
}
